import { Injectable } from '@angular/core';
import { item } from '@scaffold/editor/items';
import { items } from '@scaffold/editor/items_list';
import { Observable, of, Subject, BehaviorSubject } from 'rxjs';
import { Router, NavigationStart } from '@angular/router';
import cloneDeep from 'lodash/cloneDeep';
import { Info } from '../view/rendered/rendered.service';
import { environment } from '@env/environment';
import { HttpUtilService } from '@services/http-util.service';
import { UpdatingStatusService } from '@services/updating-status.service';
import { NotificationsService } from '@services/notifications.service';
import { TranslationService } from '@services/translation.service';
import { catchError, tap } from 'rxjs/operators';
import { DeleteTypesService } from '@services/delete-types.service';
@Injectable({
  providedIn: 'root',
})
export class ItemsService {
  constructor(
    private translationService: TranslationService,
    public notifications: NotificationsService,
    public http: HttpUtilService,
    private router: Router,
    private updateStatusService: UpdatingStatusService,
    private deleteTypesService: DeleteTypesService
  ) {
    // clear all existing data on route change
    router.events.subscribe(event => {
      if (event instanceof NavigationStart) {
        this.hasAttemptedSave.next(false);
        const infos = Object.keys(this._globalData);
        infos.forEach(k => {
          const datas = Object.keys(this._globalData[k].data);
          if (datas.length === 0) {
            delete this._globalData[k];
          }
        });
      }
    });
  }
  /** Save any changes on route change*/
  autosave = false;
  /**Stores references and changes to data items*/
  private _globalData: any = {};
  /**Stores Subject references to loaded data items to sync changes to all components referencing them*/
  private _globalObservables = {};
  /**Subject to trigger any needed changes if the stored chganges are reset*/
  private _reset = new Subject<any>();
  /**Data for new data item sorted by type that hasn't been created in the API*/
  newData = {};
  public hasAttemptedSave = new BehaviorSubject<boolean>(false);
  /**Stores Subject references for new data item sorted by type that hasn't been created in the API*/
  newObservables = {};

  /***/
  viewKeys = [];

  /**Subscription to get the list of items, This may be moved to an API call in the future */
  getItems(): Observable<any[]> {
    return of(items);
  }

  /**
   * Get any existing changes to a specified data item
   * @param info Data item to get changed data for
   * @param key Specific key to get
   * @param value Original value to set if update doesn't exist
   */
  getData(info: Info, key?: string, value?: any) {
    if (info) {
      // console.log("Set Original " + item.id + ": ", key, value)
      if (info.id && info.type) {
        // console.log(item.id, this.newData);
        // console.log(this._globalData[item.id])
        const id = info.id;
        if (!this._globalData.hasOwnProperty(id)) {
          //   console.log("CREATE NEW DATA");
          this._globalData[id] = {
            type: info.type,
            data: {},
            original: {},
            errors: {},
          };
        }
        if (!this._globalObservables.hasOwnProperty(id)) {
          this._globalObservables[id] = new Subject<any>();
        }
        if (key && this._globalData[id]) {
          if (!this._globalData[id].original.hasOwnProperty(key)) {
            this.set(this._globalData[id].original, key, value);
            console.log('setting original', key, value);
          }
        }
        return this._globalData[id] ? this._globalData[id].data : null;
      } else {
        //  console.log("no data id")
        console.log(this.newData);
        if (!this.newData.hasOwnProperty(info.type)) {
          this.newData[info.type] = {
            type: info.type,
            data: {},
            original: {},
            errors: {},
          };
        }
        //   console.log(this.newData[item.type])
        return this.newData[info.type].data;
      }
    } else {
      if (key) {
        return null;
      }
      return {};
    }
  }
  /**
   * Clear any existing data set for a new data item of a specific type
   * @param type New data type to clear, if not set clear all
   */
  resetNewData(type?: string) {
    console.log(type);
    console.log(this.newData);
    if (type) {
      if (this.newData[type]) {
        Object.keys(this.newData[type]).forEach(key => {
          this.updateValue(key, null, { type: type });
        });
        this.removeNewData(type);
      }
    } else {
      Object.keys(this.newData).forEach(t => this.resetNewData(type));
    }
  }
  /**
   * Remove any data and update Subjects for a specified data type
   * @param type Data type to remove new data for
   */
  removeNewData(type: string) {
    const ty = new Subject<any>();
    delete this.newData[type];
    if (this.newObservables[type]) {
      this.newObservables[type].complete();
    }
    delete this.newObservables[type];
  }
  /**
   * Remove all change data currently stored
   * @param reset Trigger a reset update on all components subscribed to the data
   */
  cancelData(reset?: boolean) {
    const datas = Object.keys(this._globalData);
    datas.forEach(k => {
      // console.log('canceling ' + k + " " + this._globalData[k].type)
      // console.log(this._globalData[k])
      if (this._globalData[k]) {
        Object.keys(this._globalData[k].data).forEach(ke => {
          delete this._globalData[k].data[ke];
          delete this._globalData[k].errors[ke];
        });
        if (reset && this._globalObservables[k]) {
          this._reset.next('reset');
          //  console.log("resetting to:")
          //  console.log(this._globalData[k].original);
          this._globalObservables[k].next(this._globalData[k].original);
        }
      }
      if (!reset) {
        this._reset.next('saved');
      }
    });
  }
  /**
   * Update a data item in the system
   * @param url API enpoinnt to put too
   * @param data Data to put
   */
  saveViewData(url: string, data: any, query?: string[]): Observable<any> {
    if (query && query.join) {
      url += '?' + query.join('&');
    }
    return this.http.request(
      'PUT',
      environment.apiRoot + url,
      data,
      null,
      true
    );
  }
  /**
   * Create a new data item in the system
   * @param path Parent and type of data in URL format
   * @param data Data to use for new item
   * @param query Additional query params
   */
  createData(path: string, data: any, query?: string[]): Observable<any> {
    this.updateStatusService.setStatus('PROCESSING');
    if (path.match(/^\//)) {
      path = path.substring(1);
    }
    let url = environment.apiRoot + '/' + path;
    if (query && query.join) {
      url += '?' + query.join('&');
    }
    const req = this.http
      .request('POST', url, data, null, true)
      .pipe(catchError(error => of(error)))
      .pipe(
        tap(response => {
          const wasCreated = response?.id;
          this.notifications.popNotification(
            '',
            wasCreated
              ? `Successfully Created`
              : `Error Creating: ${response?.errorMessage || 'Unknown Error'}`
          );
        })
      );
    return req;
  }
  /**
   * Delete a data item from the system as specified path
   * @param path Data item to delete
   */
  deleteData(path: string, query?: string[]): Observable<any> {
    let response = new Subject();
    this.deleteTypesService.deleteType(path).subscribe(data => {
      path += '?mode=read';
      if (query && query.join) {
        query.push('mode=read');
        path += '&' + query.join('&');
      }
      this.http
        .request('DELETE', environment.apiRoot + path, null, null, true)
        .subscribe(res => {
          response.next(res);
          response.complete();
        });
    });
    return response.asObservable();
  }

  /**
   * Process an array of items through a function in batches
   * @param items Array of data items
   * @param work Function to pass each item, too, returns a promise
   * @param concurrent How many items to process at a time
   * @param status :pass overall status observable to subsequent executions
   */
  workerQueue(
    items: Array<any>,
    work: Function,
    concurrent = 10,
    status = new Subject()
  ) {
    let working = items.splice(0, concurrent);

    Promise.all(
      working.map(i => {
        console.log('start', i);
        return work(i);
      })
    ).then(() => {
      console.log('finished', working.toString());
      if (items.length) {
        this.workerQueue(items, work, concurrent, status);
      } else {
        status.next(null);
        status.complete();
        this.cancelData();
      }
    });

    return status.asObservable();
  }

  /**
   * Trigger a save API call for all changes
   * @param info Save only the specified item
   * @param key Save only the specified key
   */
  saveData(info?: Info, key?: string) {
    console.log(this._globalData);
    if (info) {
      let prom;

      console.log('Saving', info.id);
      if (
        (this._globalData[info.id] &&
          key &&
          this._globalData[info.id]?.data?.[key]) ||
        Object.keys(this._globalData[info.id]?.data || {}).length
      ) {
        console.log(this._globalData[info.id]);
        const type = this._globalData[info.id].type;
        let view_data = {};
        if (key) {
          view_data[key] = this._globalData[info.id].data[key];
        } else {
          view_data = this._globalData[info.id].data;
        }
        console.log(view_data);
        if (view_data) {
          const data = cloneDeep(view_data);
          let newurl = '';

          newurl += '/' + type + '/' + info.id;

          console.log(newurl);
          prom = new Observable(ob => {
            this.saveViewData(newurl, { data: data }).subscribe(d => {
              const wasSaved = !!d.updated;
              // error_saving
              if (!wasSaved)
                this.notifications.popNotification(
                  '',
                  `Error Saving: ${d?.errorMessage || d?.error || 'Unknown Error'}`
                );

              view_data = {};

              Object.keys(data).forEach(d_k => {
                if (this._globalData[info.id] && wasSaved) {
                  this._globalData[info.id].original[d_k] = data[d_k];
                  delete this._globalData[info.id].data[d_k];
                }
              });
              ob.next(d);
              ob.complete();
            });
          });
        }
      }
      return prom;
    } else {
      const datas = Object.keys(this._globalData);
      console.log(datas);
      return this.workerQueue(datas, k => {
        console.log('Saving', k);
        if (
          this._globalData[k] &&
          Object.keys(this._globalData[k].data).length > 0
        ) {
          let prom = new Promise((resolve, reject) => {
            console.log(this._globalData[k]);
            const type = this._globalData[k].type;
            let view_data = this._globalData[k].data;
            console.log(view_data);
            if (view_data) {
              const data = cloneDeep(view_data);
              let newurl = '';

              newurl += '/' + type + '/' + k;

              console.log(newurl);
              /*  Object.keys(data).forEach(key => {
                  if (typeof data[key] == 'object') {
                    this.searchChildrenForDataRef(data[key]);
                  }
                })*/

              try {
                this.saveViewData(newurl, { data: data }).subscribe(d => {
                  const text = this.translationService.getTranslatedContent(
                    'error_saving',
                    'Error Saving'
                  );
                  const unknowText =
                    this.translationService.getTranslatedContent(
                      'unknown_error',
                      'Unknown Error'
                    );

                  const wasSaved = !!d.updated;
                  if (!wasSaved)
                    this.notifications.popNotification(
                      '',
                      `${text}: ${d?.errorMessage || unknowText}`
                    );
                  view_data = {};
                  Object.keys(data).forEach(d_k => {
                    if (this._globalData[k] && wasSaved) {
                      this._globalData[k].original[d_k] = data[d_k];
                    }
                    resolve(true);
                  });
                });
              } catch (error) {
                const text = this.translationService.getTranslatedContent(
                  'failed_to_save',
                  'Failed to Save'
                );
                error.name = `${text}: ${error.name}`;
                throw error;
              }
            }
          });
          return prom;
        } else {
          console.log('Nothing to save');
          return of({});
        }
      });
    }
  }
  /**
   * Check if there are any changes to determin if the save button should be vissible
   */
  showSave() {
    let hasData = false;
    const datas = Object.keys(this._globalData);
    datas.forEach(k => {
      if (
        this._globalData[k] &&
        Object.keys(this._globalData[k].data).length > 0
      ) {
        hasData = true;
      }
    });
    return hasData;
  }
  /**
   * Check if the same button should be disabled due to an error in an input
   * @param newitem If the check should be done for a new item being created
   */
  disableSave(newitem?) {
    let hasError;
    // check all existing data for errors or missing requireds
    if (!newitem) {
      const datas = Object.keys(this._globalData);
      datas.forEach(k => {
        if (
          this._globalData[k] &&
          Object.keys(this._globalData[k].errors).length > 0 &&
          !hasError
        ) {
          hasError = JSON.stringify(this._globalData[k].errors);
        }
      });
    } else {
      // check new items for errors or missing requireds
      if (this.newData[newitem]) {
        if (Object.keys(this.newData[newitem].errors).length > 0) {
          hasError = JSON.stringify(this.newData[newitem].errors);
        }
      }
    }
    return hasError;
  }

  /**
   * Register an item to watch for changes on. if the item has previously been registered, return the existi9ng Subject for it.
   * @param info Item to register
   */
  registerItem(info: Info) {
    if (info) {
      if (info.id && info.type) {
        const id = info.id;
        if (!this._globalData.hasOwnProperty(id)) {
          //      console.log(item);
          this._globalData[id] = {
            type: info.type,
            data: {},
            original: {},
            errors: {},
          };
        }
        if (!this._globalObservables.hasOwnProperty(id)) {
          this._globalObservables[id] = new Subject<any>();
        }
        return this._globalObservables[id].asObservable();
      } else {
        //  console.log("no data id")
        // console.log(item);
        if (!this.newData.hasOwnProperty(info.type)) {
          this.newData[info.type] = {
            type: info.type,
            data: {},
            original: {},
            errors: {},
          };
        }
        if (!this.newObservables.hasOwnProperty(info.type)) {
          this.newObservables[info.type] = new Subject<any>();
        }
        return this.newObservables[info.type].asObservable();
      }
    } else {
      return new Subject<any>();
    }
  }

  /**Watch for a reset event */
  registerReset() {
    return this._reset.asObservable();
  }

  /**
   * set the data value, supports dot notation including array index ie.  ({}, "some.0.thing", true) will set  {"some":[{"thing":true}]}
   * @param obj Object to set the value on
   * @param param String parameter to use for the address of the data to set
   * @param val Value to set
   */
  set(obj, param: string, val: any) {
    const str = param;
    if (str.match(/\//)) {
      let keys, key;
      // make sure str is a string with length
      if (
        !str ||
        !str.length ||
        Object.prototype.toString.call(str) !== '[object String]'
      ) {
        return false;
      }
      if (obj !== Object(obj)) {
        // if it's not an object, make it one
        obj = {};
      }
      keys = str.split('/');
      while (keys.length > 1) {
        key = keys.shift();
        if (obj !== Object(obj)) {
          // if it's not an object, make it one
          if (key.match(/\D/)) {
            obj = {};
          } else {
            obj = [];
          }
        }
        if (!(key in obj)) {
          // if obj doesn't contain the key, add it and set it to an empty object
          if (keys[0] && !keys[0].match(/\D/)) {
            obj[key] = [];
          } else {
            obj[key] = {};
          }
        }
        obj = obj[key];
      }
      return (obj[keys[0]] = val);
    } else {
      return (obj[str] = val);
    }
  }
  /**
   * Delete a key from an object, supports dot notation including array index
   * ie. * ({}, "some.0.thing") will delete 'thing' from  {"some":[{"thing":true}]}
   * @param obj Object to remove the key from
   * @param param Key to remove
   */
  del(obj, param: string) {
    const str = param;
    if (str.match(/\//)) {
      let keys, key;
      // make sure str is a string with length
      if (
        !str ||
        !str.length ||
        Object.prototype.toString.call(str) !== '[object String]'
      ) {
        return false;
      }
      if (obj !== Object(obj)) {
        // if it's not an object, make it one
        obj = {};
      }
      keys = str.split('/');
      while (keys.length > 1) {
        key = keys.shift();
        if (obj !== Object(obj)) {
          // if it's not an object, make it one
          if (key.match(/\D/)) {
            obj = {};
          } else {
            obj = [];
          }
        }
        if (!(key in obj)) {
          // if obj doesn't contain the key, add it and set it to an empty object
          if (keys[0] && !keys[0].match(/\D/)) {
            obj[key] = [];
          } else {
            obj[key] = {};
          }
        }
        obj = obj[key];
      }
      delete obj[keys[0]];
    } else {
      delete obj[str];
    }
  }

  /**
   * returns a value from data. supprots dot notation including array index
   * ie. ({"some":[{"thing":true}]},"some.0.thing") will return true
   * @param obj Object to get value on
   * @param str Key to get the value from
   */
  get(obj, str): any {
    str = str.split('/');
    for (let i = 0; i < str.length; i++) {
      if (obj) {
        obj = obj[str[i]];
      } else {
        obj = undefined;
        i = str.length;
      }
    }
    return obj;
  }
  /**
   * Check if the specified key exists on the specified object
   * @param obj Object to check
   * @param str Ket to check
   */
  hasKey(obj, str: string): boolean {
    const orStr = str;
    const orObj = obj;
    let hasKey = false;
    const parts = str.split('/');
    if (obj) {
      for (let i = 0; i < parts.length; i++) {
        if (obj.hasOwnProperty(parts[i])) {
          if (i === parts.length - 1) {
            hasKey = true;
          } else {
            obj = obj[parts[i]];
          }
        } else {
          i = parts.length;
        }
      }
    }
    return hasKey;
  }
  /**
   * Set the value for a specific key on a data item
   * @param name Key to set
   * @param value Value to set
   * @param info Data item to update
   * @param error Any errors that may exist on the source input
   * @param del Remove the key completly from the data item
   */
  updateValue(name: string, value: any, info: Info, error?, del?) {
    setTimeout(_ => {
      if (info) {
        if (!this._globalData.hasOwnProperty(info.id)) {
          this._globalData[info.id] = {
            type: info.type,
            data: {},
            original: {},
            errors: {},
          };
        }
        if (!this._globalObservables.hasOwnProperty(info.id)) {
          this._globalObservables[info.id] = new Subject<any>();
        }
        if (info.id) {
          if (!error) {
            delete this._globalData[info.id].errors[name];
            // if a data array is being updated grabe the value of the array from the root item toallow for complete updates.
            // Needed for adding and removing items to the data array
            const base = name.split(/\//)[0];
            // console.log(this._globalData[item.id].data[base])
            if (
              !this._globalData[info.id].data.hasOwnProperty(base) ||
              (typeof value === 'object' &&
                typeof this._globalData[info.id].data[base] !== 'object')
            ) {
              this._globalData[info.id].data[base] =
                cloneDeep(this._globalData[info.id].original[base]) || [];
            }
            if (del) {
              this.del(this._globalData[info.id].data, name);
            } else {
              this.set(this._globalData[info.id].data, name, value);
            }
            this._globalObservables[info.id].next(
              this._globalData[info.id].data
            );
          } else {
            this._globalData[info.id].errors[name] = error;
          }
        } else {
          if (this.newData[info.type]) {
            if (!error) {
              delete this.newData[info.type].errors[name];
              if (del) {
                this.del(this.newData[info.type].data, name);
              } else {
                this.set(this.newData[info.type].data, name, value);
              }
              this.newObservables[info.type].next(this.newData[info.type].data);
            } else {
              this.newData[info.type].errors[name] = error;
            }
          }
        }
      }
    });
  }
  /**
   * Get value of specific key on a data item
   * @param name Key to get
   * @param info Object to look on
   */
  getValue(name: string, info: Info): any {
    if (info) {
      if (info.id && this._globalData[info.id]) {
        return this.get(this._globalData[info.id].data, name);
      } else {
        if (this.newData[info.type]) {
          return this.get(this.newData[info.type].data, name);
        }
      }
    }
  }
  /**TEMP get a list of items that have existing changes */
  getGlobalData(): string {
    let Global = 'Changes to: ';
    const changes = [];
    Object.keys(this._globalData).forEach(id => {
      const info = this._globalData[id];
      if (Object.keys(info.data).length > 0) {
        changes.push(
          (info.data.title
            ? info.data.title
            : info.original.title
              ? info.original.title
              : info.type + ' ' + id) + ''
        );
      }
    });
    Global += changes.join(', ');
    return Global;
  }

  /**
   * Store keys
   * @param viewKeys Keys to store
   */
  setKeys(viewKeys: any[]) {
    this.viewKeys = viewKeys;
  }
}
